import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue'
import TerminalView from '@/views/dashboard/manage/genuine-software-usage/_components/terminal-view/terminal-view.vue'
import SoftwareView from '@/views/dashboard/manage/genuine-software-usage/_components/software-view/software-view.vue'
import PageSubscribeBar from '@/views/dashboard/_components/subscribe-bar/page-subscribe-bar.vue'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import Card from '@/views/dashboard/_components/card/card.vue'
import StatisticsCards from '@/views/dashboard/_components/statistics-cards/statistics-cards.vue'
import type { StatisticsCardItem } from '@/views/dashboard/_components/statistics-cards/types'
import RankingChart from '@/views/dashboard/_components/ranking-chart/ranking-chart.vue'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { getTimeRangeByEnum } from '@/utils/time'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import { RankCardService } from '@/views/dashboard/manage/genuine-software-usage/_components/software-view/_components/rank-card/rank-card.service'
import { RankType } from '@/views/dashboard/manage/genuine-software-usage/_components/software-view/software-view.service'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { useParseTime } from '@/hooks'
import { SoftmgrAuthenticSoftStatService } from '@/views/dashboard/manage/genuine-software-usage/service'
import { defaultAuthenticSoftUsageMonthRangeStrings } from '@/views/dashboard/manage/genuine-software-usage/_utils/default-month-range'

type GenuineUsageSoftwareDrawerEmbed = {
  authStatus?: number
  idleOnly?: boolean
  initialSoftName?: string
  /** 与排行图请求区间一致（与 TimeRange 选择一致），用于列表时间筛与请求 */
  initialDateRange?: [string, string]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'genuine-software-usage',
  setup(__props) {

const injector = useReflectiveInjector([RankCardService, SoftmgrAuthenticSoftStatService])
const servicePermissionService = injector.get(ServicePermissionService)
const rankCardService = injector.get(RankCardService)
const softmgrAuthenticSoftStatService = injector.get(SoftmgrAuthenticSoftStatService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.soft_authorization)

// 统计：未授权 = install_count；已授权 = auth_soft_count；闲置/最高频与接口字段一致（见 AuthSoftResponseData）
const statistics = reactive({
  unauthorizedGenuineCount: 0,
  authorizedGenuineCount: 0,
  /** 闲置正版软件（近7天未使用过的软件数量） */
  idleGenuineCount: 0,
  /** 最高频率使用正版软件名称 */
  maxTimesSoft: '',
})

const { t, locale } = useI18n()

const softwareDrawerEmbedDefaults = ref<GenuineUsageSoftwareDrawerEmbed | null>(null)

function openSoftwareListDrawer(embed: GenuineUsageSoftwareDrawerEmbed | null = null) {
  if (!hasPermission) return
  softwareDrawerEmbedDefaults.value = embed
  softwareDrawerListKey.value += 1
  softwareDrawerVisible.value = true
}

const statisticsItems = computed((): StatisticsCardItem[] => {
  void locale.value
  return [
    {
      label: t('genuineSoftwareUsage.statUnauthorized'),
      value: statistics.unauthorizedGenuineCount,
      clickable: statistics.unauthorizedGenuineCount > 0,
      events: {
        click: () => {
          if (statistics.unauthorizedGenuineCount <= 0) return
          // 授权状态 = 未授权（接口 auth_status 0）
          openSoftwareListDrawer({ authStatus: 0 })
        },
      },
    },
    {
      label: t('genuineSoftwareUsage.statAuthorized'),
      value: statistics.authorizedGenuineCount,
      clickable: statistics.authorizedGenuineCount > 0,
      events: {
        click: () => {
          if (statistics.authorizedGenuineCount <= 0) return
          // 授权状态 = 已授权（接口 auth_status 2）
          openSoftwareListDrawer({ authStatus: 2 })
        },
      },
    },
    {
      label: t('genuineSoftwareUsage.statIdle'),
      value: statistics.idleGenuineCount,
      clickable: statistics.idleGenuineCount > 0,
      events: {
        click: () => {
          if (statistics.idleGenuineCount <= 0) return
          openSoftwareListDrawer({ idleOnly: true })
        },
      },
    },
    {
      label: t('genuineSoftwareUsage.statMostFrequent'),
      value: statistics.maxTimesSoft || '--',
      clickable: Boolean(statistics.maxTimesSoft),
      events: {
        click: () => {
          if (!statistics.maxTimesSoft) return
          openSoftwareListDrawer({ initialSoftName: statistics.maxTimesSoft })
        },
      },
    },
  ]
})

// 图表数据
const durationRankingData = ref<Array<{ name: string; count: number }>>([])
const installationRankingData = ref<Array<{ name: string; count: number }>>([])
const durationRankingLoading = ref(false)
const installationRankingLoading = ref(false)
const durationRankingChartRef = ref()
const installationRankingChartRef = ref()

/** 两个排行图共用时间段，默认近 30 天（最近一个月） */
const rankingTimeRange = ref<TimeRange>(TimeRange.DAYS_30)

// 抽屉
const softwareDrawerVisible = ref(false)
const softwareDrawerListKey = ref(0)
const terminalDrawerVisible = ref(false)

function viewAllSoftware() {
  if (!hasPermission) return
  openSoftwareListDrawer(null)
}

function viewAllTerminals() {
  if (!hasPermission) return
  terminalDrawerVisible.value = true
}

/** 将排行 TimeRange 转为自然日 YYYY-MM-DD 区间，供 useParseTime / 抽屉 initialDateRange */
function getRankingDateRangeStrings(): [string, string] | null {
  const tr = rankingTimeRange.value
  if (tr === TimeRange.ALL) return null
  // 与 software-view 列表默认「最近一个月」一致（上月同日～今天），勿用 getTimeRangeByEnum 的滚动 30 天
  if (tr === TimeRange.DAYS_30) {
    return defaultAuthenticSoftUsageMonthRangeStrings()
  }
  const [startSec, endSec] = getTimeRangeByEnum(tr)
  if (startSec == null || endSec == null) return null
  return [dayjs.unix(startSec).format('YYYY-MM-DD'), dayjs.unix(endSec).format('YYYY-MM-DD')]
}

function getRankingRangeDayCount(range: [string, string] | null): number {
  if (!range?.[0] || !range[1]) {
    const d = defaultAuthenticSoftUsageMonthRangeStrings()
    return Math.max(1, dayjs(d[1]).diff(dayjs(d[0]), 'day') + 1)
  }
  return Math.max(1, dayjs(range[1]).diff(dayjs(range[0]), 'day') + 1)
}

function handleDurationRankingItemClick(item: { name?: string; count?: number }) {
  if (!hasPermission) return
  if ((item?.count ?? 0) <= 0) return
  const name = (item?.name ?? '').trim()
  if (!name) return
  const dr = getRankingDateRangeStrings()
  openSoftwareListDrawer({
    initialSoftName: name,
    ...(dr ? { initialDateRange: dr } : {}),
  })
}

function handleInstallationRankingItemClick(item: { name?: string; count?: number }) {
  if (!hasPermission) return
  if ((item?.count ?? 0) <= 0) return
  const name = (item?.name ?? '').trim()
  if (!name) return
  const dr = getRankingDateRangeStrings()
  openSoftwareListDrawer({
    initialSoftName: name,
    ...(dr ? { initialDateRange: dr } : {}),
  })
}

// 加载统计数据
async function loadStatistics() {
  if (!hasPermission) return

  try {
    const res = await softmgrAuthenticSoftStatService.getSoftmgrAuthSoftUseStat()
    statistics.unauthorizedGenuineCount = res.install_count ?? 0
    statistics.authorizedGenuineCount = res.auth_soft_count ?? 0
    statistics.idleGenuineCount = res.total_count ?? 0
    statistics.maxTimesSoft = (res.max_times_soft && String(res.max_times_soft).trim()) || ''
  } catch {
    statistics.unauthorizedGenuineCount = 0
    statistics.authorizedGenuineCount = 0
    statistics.idleGenuineCount = 0
    statistics.maxTimesSoft = ''
  }
}

// 加载使用时长排行数据
async function loadDurationRankingData() {
  if (!hasPermission) return

  if (durationRankingChartRef.value) {
    durationRankingLoading.value = true
  }
  try {
    const dateStrings = getRankingDateRangeStrings()
    const days = getRankingRangeDayCount(dateStrings)
    const rangeForParse: [string, string] = dateStrings ?? defaultAuthenticSoftUsageMonthRangeStrings()
    const [st, et] = useParseTime(rangeForParse)
    const res = await rankCardService.getRankData({
      start_time: st != null ? String(st) : undefined,
      end_time: et != null ? String(et) : undefined,
      type: RankType.duration,
      authenticsoft_flag: 1,
      is_mock: false,
    })

    const top5 = (res.view_infos || [])
      .filter(v => v.value)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)
      .map((item, index) => ({
        name: item.name || t('genuineSoftwareUsage.unknown'),
        count: Math.round((item.value / 60) / days), // 分钟→小时，日均，四舍五入取整
        rank: index + 1
      }))

    durationRankingData.value = top5
  } catch (error) {
    console.error('加载使用时长排行数据失败:', error)
    durationRankingData.value = []
  } finally {
    if (durationRankingChartRef.value) {
      durationRankingLoading.value = false
    }
  }
}

// 加载安装情况排行数据
async function loadInstallationRankingData() {
  if (!hasPermission) return

  if (installationRankingChartRef.value) {
    installationRankingLoading.value = true
  }
  try {
    const dateStrings = getRankingDateRangeStrings()
    const rangeForParse: [string, string] = dateStrings ?? defaultAuthenticSoftUsageMonthRangeStrings()
    const [st, et] = useParseTime(rangeForParse)
    const res = await rankCardService.getRankData({
      start_time: st != null ? String(st) : undefined,
      end_time: et != null ? String(et) : undefined,
      type: RankType.terminalCount,
      authenticsoft_flag: 1,
      is_mock: false,
    })

    const top5 = (res.view_infos || [])
      .filter(v => v.value)
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)
      .map((item, index) => ({
        name: item.name || t('genuineSoftwareUsage.unknown'),
        count: item.value,
        rank: index + 1
      }))

    installationRankingData.value = top5
  } catch (error) {
    console.error('加载安装情况排行数据失败:', error)
    installationRankingData.value = []
  } finally {
    if (installationRankingChartRef.value) {
      installationRankingLoading.value = false
    }
  }
}

watch(rankingTimeRange, () => {
  if (!hasPermission) return
  loadDurationRankingData()
  loadInstallationRankingData()
})

onMounted(async () => {
  await nextTick()
  if (hasPermission) {
    loadStatistics()
    loadDurationRankingData()
    loadInstallationRankingData()
  }
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PageSubscribeBar, {
      "normal-permission-key": _unref(PermissionFe).soft_authorization
    }, null, 8, ["normal-permission-key"]),
    (_unref(hasPermission))
      ? (_openBlock(), _createBlock(StatisticsCards, {
          key: 0,
          items: statisticsItems.value,
          class: "cm-ui-mb16"
        }, null, 8, ["items"]))
      : _createCommentVNode("", true),
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "charts-row"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(RankingChart, {
              ref_key: "durationRankingChartRef",
              ref: durationRankingChartRef,
              title: _ctx.$t('genuineSoftwareUsage.durationRankTitle'),
              data: durationRankingData.value,
              "show-time-range": false,
              "time-range": rankingTimeRange.value,
              "onUpdate:timeRange": _cache[0] || (_cache[0] = ($event: any) => ((rankingTimeRange).value = $event)),
              "show-index": true,
              unit: _ctx.$t('genuineSoftwareUsage.hour'),
              "card-size": "second",
              loading: durationRankingLoading.value,
              "onUpdate:loading": _cache[1] || (_cache[1] = ($event: any) => ((durationRankingLoading).value = $event)),
              "row-clickable": _unref(hasPermission),
              onItemClick: handleDurationRankingItemClick
            }, {
              titleAfter: _withCtx(() => [
                _createVNode(_component_el_tag, {
                  size: "small",
                  type: "primary"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["title", "data", "time-range", "unit", "loading", "row-clickable"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(RankingChart, {
              ref_key: "installationRankingChartRef",
              ref: installationRankingChartRef,
              title: _ctx.$t('genuineSoftwareUsage.installationRankTitle'),
              data: installationRankingData.value,
              "show-time-range": false,
              "time-range": rankingTimeRange.value,
              "onUpdate:timeRange": _cache[2] || (_cache[2] = ($event: any) => ((rankingTimeRange).value = $event)),
              "show-index": true,
              unit: "",
              "card-size": "second",
              loading: installationRankingLoading.value,
              "onUpdate:loading": _cache[3] || (_cache[3] = ($event: any) => ((installationRankingLoading).value = $event)),
              "row-clickable": _unref(hasPermission),
              onItemClick: handleInstallationRankingItemClick
            }, {
              titleAfter: _withCtx(() => [
                _createVNode(_component_el_tag, {
                  size: "small",
                  type: "primary"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["title", "data", "time-range", "loading", "row-clickable"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('genuineSoftwareUsage.softwareListTitle'),
      class: "cm-ui-mt16",
      onViewMore: viewAllSoftware,
      "view-more-disabled": !_unref(hasPermission)
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(SoftwareView, {
          ref: "softwareViewRef",
          "view-mode": _unref(TableViewMode).preview,
          "has-permission": _unref(hasPermission),
          onViewAll: viewAllSoftware
        }, null, 8, ["view-mode", "has-permission"])
      ]),
      _: 1
    }, 8, ["title", "view-more-disabled"]),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('genuineSoftwareUsage.terminalListTitle'),
      class: "cm-ui-mt16",
      onViewMore: viewAllTerminals,
      "view-more-disabled": !_unref(hasPermission)
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(TerminalView, {
          ref: "terminalViewRef",
          "view-mode": _unref(TableViewMode).preview,
          "has-permission": _unref(hasPermission),
          onViewAll: viewAllTerminals
        }, null, 8, ["view-mode", "has-permission"])
      ]),
      _: 1
    }, 8, ["title", "view-more-disabled"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('genuineSoftwareUsage.allSoftwareList'),
      modelValue: softwareDrawerVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((softwareDrawerVisible).value = $event)),
      "destroy-on-close": "",
      "append-to-body": ""
    }, {
      default: _withCtx(() => [
        (softwareDrawerVisible.value)
          ? (_openBlock(), _createBlock(SoftwareView, {
              key: softwareDrawerListKey.value,
              "has-permission": _unref(hasPermission),
              "view-mode": _unref(TableViewMode).full,
              "embed-defaults": softwareDrawerEmbedDefaults.value
            }, null, 8, ["has-permission", "view-mode", "embed-defaults"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('genuineSoftwareUsage.allTerminalList'),
      modelValue: terminalDrawerVisible.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((terminalDrawerVisible).value = $event)),
      "destroy-on-close": "",
      "append-to-body": ""
    }, {
      default: _withCtx(() => [
        _createVNode(TerminalView, {
          "has-permission": _unref(hasPermission),
          "view-mode": _unref(TableViewMode).full
        }, null, 8, ["has-permission", "view-mode"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"])
  ], 64))
}
}

})